/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Impero Org API
 * The Impero Backend's API for organizations, i.e. orgname.impero.com.
 */
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
    UseSuspenseQueryOptions,
    UseSuspenseQueryResult,
} from "@tanstack/react-query";

import axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type {
    AccessToken,
    Authenticated,
    ChangePasswordData,
    Email,
    FindOrgByName,
    GetResetPasswordParams,
    GetSingleSignOnParams,
    LoginOption,
    LoginTimeout,
    OrgName,
    PostResetPasswordParams,
    RedirectTarget,
    RequestResetPasswordData,
    SendOrgChooserEmailData,
    SetPasswordData,
    SsoBehavior,
    Unauthenticated,
    UserAndPassword,
    UserView,
} from "../model";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

const withQueryKey = <T extends object, K>(
    query: T,
    queryKey: K
): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === "queryKey") continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

export const postAccessToken = (
    accessToken: AccessToken,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<OrgName[] | null>> => {
    return axios.post(`/api/access-token`, accessToken, options);
};

export const getPostAccessTokenMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postAccessToken>>,
        TError,
        { data: AccessToken },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postAccessToken>>,
    TError,
    { data: AccessToken },
    TContext
> => {
    const mutationKey = ["postAccessToken"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postAccessToken>>,
        { data: AccessToken }
    > = (props) => {
        const { data } = props ?? {};

        return postAccessToken(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostAccessTokenMutationResult = NonNullable<
    Awaited<ReturnType<typeof postAccessToken>>
>;
export type PostAccessTokenMutationBody = AccessToken;
export type PostAccessTokenMutationError = AxiosError<unknown>;

export const usePostAccessToken = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postAccessToken>>,
            TError,
            { data: AccessToken },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postAccessToken>>,
    TError,
    { data: AccessToken },
    TContext
> => {
    return useMutation(getPostAccessTokenMutationOptions(options), queryClient);
};
export const postChangePassword = (
    changePasswordData: ChangePasswordData,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Authenticated>> => {
    return axios.post(`/api/change-password`, changePasswordData, options);
};

export const getPostChangePasswordMutationOptions = <
    TError = AxiosError<Unauthenticated | LoginTimeout>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postChangePassword>>,
        TError,
        { data: ChangePasswordData },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postChangePassword>>,
    TError,
    { data: ChangePasswordData },
    TContext
> => {
    const mutationKey = ["postChangePassword"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postChangePassword>>,
        { data: ChangePasswordData }
    > = (props) => {
        const { data } = props ?? {};

        return postChangePassword(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostChangePasswordMutationResult = NonNullable<
    Awaited<ReturnType<typeof postChangePassword>>
>;
export type PostChangePasswordMutationBody = ChangePasswordData;
export type PostChangePasswordMutationError = AxiosError<
    Unauthenticated | LoginTimeout
>;

export const usePostChangePassword = <
    TError = AxiosError<Unauthenticated | LoginTimeout>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postChangePassword>>,
            TError,
            { data: ChangePasswordData },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postChangePassword>>,
    TError,
    { data: ChangePasswordData },
    TContext
> => {
    return useMutation(
        getPostChangePasswordMutationOptions(options),
        queryClient
    );
};
export const postEnterOrg = (
    findOrgByName: FindOrgByName,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<RedirectTarget>> => {
    return axios.post(`/api/enter-org`, findOrgByName, options);
};

export const getPostEnterOrgMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postEnterOrg>>,
        TError,
        { data: FindOrgByName },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postEnterOrg>>,
    TError,
    { data: FindOrgByName },
    TContext
> => {
    const mutationKey = ["postEnterOrg"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postEnterOrg>>,
        { data: FindOrgByName }
    > = (props) => {
        const { data } = props ?? {};

        return postEnterOrg(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostEnterOrgMutationResult = NonNullable<
    Awaited<ReturnType<typeof postEnterOrg>>
>;
export type PostEnterOrgMutationBody = FindOrgByName;
export type PostEnterOrgMutationError = AxiosError<unknown>;

export const usePostEnterOrg = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postEnterOrg>>,
            TError,
            { data: FindOrgByName },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postEnterOrg>>,
    TError,
    { data: FindOrgByName },
    TContext
> => {
    return useMutation(getPostEnterOrgMutationOptions(options), queryClient);
};
export const postLogin = (
    userAndPassword: UserAndPassword,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Authenticated>> => {
    return axios.post(`/api/login`, userAndPassword, options);
};

export const getPostLoginMutationOptions = <
    TError = AxiosError<Unauthenticated | LoginTimeout>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postLogin>>,
        TError,
        { data: UserAndPassword },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postLogin>>,
    TError,
    { data: UserAndPassword },
    TContext
> => {
    const mutationKey = ["postLogin"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postLogin>>,
        { data: UserAndPassword }
    > = (props) => {
        const { data } = props ?? {};

        return postLogin(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostLoginMutationResult = NonNullable<
    Awaited<ReturnType<typeof postLogin>>
>;
export type PostLoginMutationBody = UserAndPassword;
export type PostLoginMutationError = AxiosError<Unauthenticated | LoginTimeout>;

export const usePostLogin = <
    TError = AxiosError<Unauthenticated | LoginTimeout>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postLogin>>,
            TError,
            { data: UserAndPassword },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postLogin>>,
    TError,
    { data: UserAndPassword },
    TContext
> => {
    return useMutation(getPostLoginMutationOptions(options), queryClient);
};
export const postLoginOptions = (
    email: Email,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<LoginOption>> => {
    return axios.post(`/api/login-options`, email, options);
};

export const getPostLoginOptionsMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postLoginOptions>>,
        TError,
        { data: Email },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postLoginOptions>>,
    TError,
    { data: Email },
    TContext
> => {
    const mutationKey = ["postLoginOptions"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postLoginOptions>>,
        { data: Email }
    > = (props) => {
        const { data } = props ?? {};

        return postLoginOptions(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostLoginOptionsMutationResult = NonNullable<
    Awaited<ReturnType<typeof postLoginOptions>>
>;
export type PostLoginOptionsMutationBody = Email;
export type PostLoginOptionsMutationError = AxiosError<unknown>;

export const usePostLoginOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postLoginOptions>>,
            TError,
            { data: Email },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postLoginOptions>>,
    TError,
    { data: Email },
    TContext
> => {
    return useMutation(
        getPostLoginOptionsMutationOptions(options),
        queryClient
    );
};
export const getLoginSsoBehavior = (
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SsoBehavior>> => {
    return axios.get(`/api/login-sso-behavior`, options);
};

export const getGetLoginSsoBehaviorQueryKey = () => {
    return [`/api/login-sso-behavior`] as const;
};

export const getGetLoginSsoBehaviorQueryOptions = <
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: Partial<
        UseQueryOptions<
            Awaited<ReturnType<typeof getLoginSsoBehavior>>,
            TError,
            TData
        >
    >;
    axios?: AxiosRequestConfig;
}) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetLoginSsoBehaviorQueryKey();

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getLoginSsoBehavior>>
    > = ({ signal }) => getLoginSsoBehavior({ signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<typeof getLoginSsoBehavior>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLoginSsoBehaviorQueryResult = NonNullable<
    Awaited<ReturnType<typeof getLoginSsoBehavior>>
>;
export type GetLoginSsoBehaviorQueryError = AxiosError<unknown>;

export function useGetLoginSsoBehavior<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                    TError,
                    Awaited<ReturnType<typeof getLoginSsoBehavior>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLoginSsoBehavior<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                    TError,
                    Awaited<ReturnType<typeof getLoginSsoBehavior>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLoginSsoBehavior<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetLoginSsoBehavior<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetLoginSsoBehaviorQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetLoginSsoBehaviorSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: Partial<
        UseSuspenseQueryOptions<
            Awaited<ReturnType<typeof getLoginSsoBehavior>>,
            TError,
            TData
        >
    >;
    axios?: AxiosRequestConfig;
}) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetLoginSsoBehaviorQueryKey();

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getLoginSsoBehavior>>
    > = ({ signal }) => getLoginSsoBehavior({ signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getLoginSsoBehavior>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLoginSsoBehaviorSuspenseQueryResult = NonNullable<
    Awaited<ReturnType<typeof getLoginSsoBehavior>>
>;
export type GetLoginSsoBehaviorSuspenseQueryError = AxiosError<unknown>;

export function useGetLoginSsoBehaviorSuspense<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLoginSsoBehaviorSuspense<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLoginSsoBehaviorSuspense<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetLoginSsoBehaviorSuspense<
    TData = Awaited<ReturnType<typeof getLoginSsoBehavior>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getLoginSsoBehavior>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetLoginSsoBehaviorSuspenseQueryOptions(options);

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const postLogout = (
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/logout`, undefined, options);
};

export const getPostLogoutMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postLogout>>,
        TError,
        void,
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postLogout>>,
    TError,
    void,
    TContext
> => {
    const mutationKey = ["postLogout"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postLogout>>,
        void
    > = () => {
        return postLogout(axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostLogoutMutationResult = NonNullable<
    Awaited<ReturnType<typeof postLogout>>
>;

export type PostLogoutMutationError = AxiosError<unknown>;

export const usePostLogout = <TError = AxiosError<unknown>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postLogout>>,
            TError,
            void,
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postLogout>>,
    TError,
    void,
    TContext
> => {
    return useMutation(getPostLogoutMutationOptions(options), queryClient);
};
export const getPing = (
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.get(`/api/ping`, options);
};

export const getGetPingQueryKey = () => {
    return [`/api/ping`] as const;
};

export const getGetPingQueryOptions = <
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: Partial<
        UseQueryOptions<Awaited<ReturnType<typeof getPing>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
}) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPingQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPing>>> = ({
        signal,
    }) => getPing({ signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<typeof getPing>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPingQueryResult = NonNullable<
    Awaited<ReturnType<typeof getPing>>
>;
export type GetPingQueryError = AxiosError<unknown>;

export function useGetPing<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof getPing>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getPing>>,
                    TError,
                    Awaited<ReturnType<typeof getPing>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPing<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof getPing>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getPing>>,
                    TError,
                    Awaited<ReturnType<typeof getPing>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPing<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof getPing>>, TError, TData>
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPing<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof getPing>>, TError, TData>
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetPingQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetPingSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: Partial<
        UseSuspenseQueryOptions<
            Awaited<ReturnType<typeof getPing>>,
            TError,
            TData
        >
    >;
    axios?: AxiosRequestConfig;
}) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPingQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPing>>> = ({
        signal,
    }) => getPing({ signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getPing>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPingSuspenseQueryResult = NonNullable<
    Awaited<ReturnType<typeof getPing>>
>;
export type GetPingSuspenseQueryError = AxiosError<unknown>;

export function useGetPingSuspense<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getPing>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPingSuspense<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getPing>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPingSuspense<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getPing>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPingSuspense<
    TData = Awaited<ReturnType<typeof getPing>>,
    TError = AxiosError<unknown>,
>(
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getPing>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetPingSuspenseQueryOptions(options);

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const postRequestResetPassword = (
    requestResetPasswordData: RequestResetPasswordData,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.post(
        `/api/request-reset-password`,
        requestResetPasswordData,
        options
    );
};

export const getPostRequestResetPasswordMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postRequestResetPassword>>,
        TError,
        { data: RequestResetPasswordData },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postRequestResetPassword>>,
    TError,
    { data: RequestResetPasswordData },
    TContext
> => {
    const mutationKey = ["postRequestResetPassword"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postRequestResetPassword>>,
        { data: RequestResetPasswordData }
    > = (props) => {
        const { data } = props ?? {};

        return postRequestResetPassword(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostRequestResetPasswordMutationResult = NonNullable<
    Awaited<ReturnType<typeof postRequestResetPassword>>
>;
export type PostRequestResetPasswordMutationBody = RequestResetPasswordData;
export type PostRequestResetPasswordMutationError = AxiosError<unknown>;

export const usePostRequestResetPassword = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postRequestResetPassword>>,
            TError,
            { data: RequestResetPasswordData },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postRequestResetPassword>>,
    TError,
    { data: RequestResetPasswordData },
    TContext
> => {
    return useMutation(
        getPostRequestResetPasswordMutationOptions(options),
        queryClient
    );
};
export const getResetPassword = (
    params: GetResetPasswordParams,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<UserView>> => {
    return axios.get(`/api/reset-password`, {
        paramsSerializer: { indexes: null },
        ...options,
        params: { ...params, ...options?.params },
    });
};

export const getGetResetPasswordQueryKey = (
    params?: GetResetPasswordParams
) => {
    return [`/api/reset-password`, ...(params ? [params] : [])] as const;
};

export const getGetResetPasswordQueryOptions = <
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getGetResetPasswordQueryKey(params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getResetPassword>>
    > = ({ signal }) => getResetPassword(params, { signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<typeof getResetPassword>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetResetPasswordQueryResult = NonNullable<
    Awaited<ReturnType<typeof getResetPassword>>
>;
export type GetResetPasswordQueryError = AxiosError<unknown>;

export function useGetResetPassword<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getResetPassword>>,
                    TError,
                    Awaited<ReturnType<typeof getResetPassword>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetResetPassword<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getResetPassword>>,
                    TError,
                    Awaited<ReturnType<typeof getResetPassword>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetResetPassword<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetResetPassword<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetResetPasswordQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetResetPasswordSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getGetResetPasswordQueryKey(params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getResetPassword>>
    > = ({ signal }) => getResetPassword(params, { signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getResetPassword>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetResetPasswordSuspenseQueryResult = NonNullable<
    Awaited<ReturnType<typeof getResetPassword>>
>;
export type GetResetPasswordSuspenseQueryError = AxiosError<unknown>;

export function useGetResetPasswordSuspense<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetResetPasswordSuspense<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetResetPasswordSuspense<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetResetPasswordSuspense<
    TData = Awaited<ReturnType<typeof getResetPassword>>,
    TError = AxiosError<unknown>,
>(
    params: GetResetPasswordParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getResetPassword>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetResetPasswordSuspenseQueryOptions(
        params,
        options
    );

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const postResetPassword = (
    setPasswordData: SetPasswordData,
    params: PostResetPasswordParams,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Authenticated>> => {
    return axios.post(`/api/reset-password`, setPasswordData, {
        paramsSerializer: { indexes: null },
        ...options,
        params: { ...params, ...options?.params },
    });
};

export const getPostResetPasswordMutationOptions = <
    TError = AxiosError<Unauthenticated | LoginTimeout>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postResetPassword>>,
        TError,
        { data: SetPasswordData; params: PostResetPasswordParams },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postResetPassword>>,
    TError,
    { data: SetPasswordData; params: PostResetPasswordParams },
    TContext
> => {
    const mutationKey = ["postResetPassword"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postResetPassword>>,
        { data: SetPasswordData; params: PostResetPasswordParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return postResetPassword(data, params, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostResetPasswordMutationResult = NonNullable<
    Awaited<ReturnType<typeof postResetPassword>>
>;
export type PostResetPasswordMutationBody = SetPasswordData;
export type PostResetPasswordMutationError = AxiosError<
    Unauthenticated | LoginTimeout
>;

export const usePostResetPassword = <
    TError = AxiosError<Unauthenticated | LoginTimeout>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postResetPassword>>,
            TError,
            { data: SetPasswordData; params: PostResetPasswordParams },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postResetPassword>>,
    TError,
    { data: SetPasswordData; params: PostResetPasswordParams },
    TContext
> => {
    return useMutation(
        getPostResetPasswordMutationOptions(options),
        queryClient
    );
};
export const postSendOrgChooserEmail = (
    sendOrgChooserEmailData: SendOrgChooserEmailData,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.post(
        `/api/send-org-chooser-email`,
        sendOrgChooserEmailData,
        options
    );
};

export const getPostSendOrgChooserEmailMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postSendOrgChooserEmail>>,
        TError,
        { data: SendOrgChooserEmailData },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postSendOrgChooserEmail>>,
    TError,
    { data: SendOrgChooserEmailData },
    TContext
> => {
    const mutationKey = ["postSendOrgChooserEmail"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postSendOrgChooserEmail>>,
        { data: SendOrgChooserEmailData }
    > = (props) => {
        const { data } = props ?? {};

        return postSendOrgChooserEmail(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostSendOrgChooserEmailMutationResult = NonNullable<
    Awaited<ReturnType<typeof postSendOrgChooserEmail>>
>;
export type PostSendOrgChooserEmailMutationBody = SendOrgChooserEmailData;
export type PostSendOrgChooserEmailMutationError = AxiosError<unknown>;

export const usePostSendOrgChooserEmail = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postSendOrgChooserEmail>>,
            TError,
            { data: SendOrgChooserEmailData },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postSendOrgChooserEmail>>,
    TError,
    { data: SendOrgChooserEmailData },
    TContext
> => {
    return useMutation(
        getPostSendOrgChooserEmailMutationOptions(options),
        queryClient
    );
};
export const getSingleSignOn = (
    params?: GetSingleSignOnParams,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.get(`/api/single-sign-on`, {
        paramsSerializer: { indexes: null },
        ...options,
        params: { ...params, ...options?.params },
    });
};

export const getGetSingleSignOnQueryKey = (params?: GetSingleSignOnParams) => {
    return [`/api/single-sign-on`, ...(params ? [params] : [])] as const;
};

export const getGetSingleSignOnQueryOptions = <
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getGetSingleSignOnQueryKey(params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getSingleSignOn>>
    > = ({ signal }) => getSingleSignOn(params, { signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<typeof getSingleSignOn>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSingleSignOnQueryResult = NonNullable<
    Awaited<ReturnType<typeof getSingleSignOn>>
>;
export type GetSingleSignOnQueryError = AxiosError<unknown>;

export function useGetSingleSignOn<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params: undefined | GetSingleSignOnParams,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getSingleSignOn>>,
                    TError,
                    Awaited<ReturnType<typeof getSingleSignOn>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSingleSignOn<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getSingleSignOn>>,
                    TError,
                    Awaited<ReturnType<typeof getSingleSignOn>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSingleSignOn<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetSingleSignOn<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetSingleSignOnQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetSingleSignOnSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getGetSingleSignOnQueryKey(params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getSingleSignOn>>
    > = ({ signal }) => getSingleSignOn(params, { signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getSingleSignOn>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSingleSignOnSuspenseQueryResult = NonNullable<
    Awaited<ReturnType<typeof getSingleSignOn>>
>;
export type GetSingleSignOnSuspenseQueryError = AxiosError<unknown>;

export function useGetSingleSignOnSuspense<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params: undefined | GetSingleSignOnParams,
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSingleSignOnSuspense<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSingleSignOnSuspense<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetSingleSignOnSuspense<
    TData = Awaited<ReturnType<typeof getSingleSignOn>>,
    TError = AxiosError<unknown>,
>(
    params?: GetSingleSignOnParams,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getSingleSignOn>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetSingleSignOnSuspenseQueryOptions(
        params,
        options
    );

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const postSingleSignOn = (
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/single-sign-on`, undefined, options);
};

export const getPostSingleSignOnMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postSingleSignOn>>,
        TError,
        void,
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postSingleSignOn>>,
    TError,
    void,
    TContext
> => {
    const mutationKey = ["postSingleSignOn"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postSingleSignOn>>,
        void
    > = () => {
        return postSingleSignOn(axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostSingleSignOnMutationResult = NonNullable<
    Awaited<ReturnType<typeof postSingleSignOn>>
>;

export type PostSingleSignOnMutationError = AxiosError<unknown>;

export const usePostSingleSignOn = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postSingleSignOn>>,
            TError,
            void,
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postSingleSignOn>>,
    TError,
    void,
    TContext
> => {
    return useMutation(
        getPostSingleSignOnMutationOptions(options),
        queryClient
    );
};
